import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router";

import { ApiClientProvider } from "@/api/ApiContext";
import { createApiClient } from "@/api/client";
import { createQueryClient } from "@/api/queryClient";
import { createAuthAdapter as defaultCreateAuthAdapter } from "@/auth/createAuthAdapter";
import {
  clearLocalSignOutMarker,
  hasLocalSignOutMarker,
  isSigningOut,
  subscribeRemoteSignOut,
} from "@/auth/signOutState";
import { AuthInitializationError, type AuthAdapter } from "@/auth/types";
import { Button } from "@/ui/Button";
import { RuntimeConfigProvider } from "@/config/RuntimeConfigContext";
import {
  RuntimeConfigError,
  loadRuntimeConfig as defaultLoadRuntimeConfig,
} from "@/config/runtimeConfig";
import type { RuntimeConfig } from "@/config/schema";
import { logger } from "@/lib/logger";
import { LocalDevProvider } from "@/localdev/LocalDevProvider";
import { TooltipProvider } from "@/ui/Tooltip";

import { AppErrorBoundary, ReloadButton } from "./AppErrorBoundary";
import { FailClosedScreen, RetryButton } from "./FailClosedScreen";
import { SessionGate } from "./SessionGate";
import { routes } from "./routes";

/**
 * Boot sequence.
 *
 * Runtime configuration, then identity, then session — in that order, with no
 * step allowed to proceed on a guess. Every failure renders a screen naming the
 * cause; none of them fall back to a default tenant, a default endpoint, or an
 * anonymous identity.
 */

type BootState =
  | { phase: "loading" }
  | { phase: "config-failed"; error: RuntimeConfigError }
  | { phase: "auth-failed"; error: AuthInitializationError }
  | { phase: "signing-in" }
  /**
   * Sign-out could not reach the provider, so only local state was cleared
   * (#44). Rendered instead of auto-starting a sign-in, because auto-start
   * against the surviving Entra SSO session would silently restore the very
   * account the user just signed out.
   */
  | { phase: "signed-out-local"; adapter: AuthAdapter }
  /** Another tab of this origin signed out; this tab must not continue. */
  | { phase: "signed-out-remote"; adapter: AuthAdapter | null }
  /** Any other terminal startup failure, including ones not anticipated here. */
  | { phase: "startup-failed"; title: string; summary: string; details: string[] }
  | { phase: "ready"; config: RuntimeConfig; adapter: AuthAdapter };

export interface BootstrapProps {
  /** Test seam. Production always loads /config.json. */
  loadConfig?: typeof defaultLoadRuntimeConfig;
  /** Test seam. Production always selects the adapter from configuration. */
  createAuthAdapter?: typeof defaultCreateAuthAdapter;
}

export function Bootstrap({
  loadConfig = defaultLoadRuntimeConfig,
  createAuthAdapter = defaultCreateAuthAdapter,
}: BootstrapProps = {}) {
  const [state, setState] = useState<BootState>({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);
  /**
   * The most recently constructed adapter, for the two listeners below that
   * outlive any single boot attempt. A ref rather than state: neither
   * listener should re-subscribe when boot progresses.
   */
  const adapterRef = useRef<AuthAdapter | null>(null);
  /**
   * Latched by the remote sign-out listener and never released within this
   * page's lifetime. A broadcast can arrive while `boot()` is mid-flight —
   * even before an adapter exists — and `setState` alone cannot stop that
   * attempt: when its pending step resolves, it would overwrite the
   * signed-out screen with automatic sign-in against the very session
   * another tab just ended. `boot()` consults this at every resumption
   * point, which makes the remote sign-out terminal.
   */
  const remoteSignOutRef = useRef(false);

  useEffect(() => {
    // Another tab announced a sign-out. Clear this tab's own cached session —
    // its per-tab storage still holds tokens — and stop, showing how to
    // continue. Rendering a static state instead of reloading is deliberate:
    // a reload would race the other tab's provider end-session redirect and
    // could silently sign the old account back in.
    return subscribeRemoteSignOut(() => {
      remoteSignOutRef.current = true;
      const adapter = adapterRef.current;
      if (adapter) {
        void adapter.clearLocalSession();
      }
      setState({ phase: "signed-out-remote", adapter });
    });
  }, []);

  useEffect(() => {
    // Back/forward-cache guard (#44's back-button criterion): navigating Back
    // after a sign-out redirect can restore the authenticated page snapshot
    // without running any script. When that snapshot belongs to a session
    // that was signed out, reload — boot then renders a signed-out state.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && (isSigningOut() || hasLocalSignOutMarker())) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // True when this attempt no longer owns the screen: the effect was
    // superseded, or a remote sign-out latched. Every failure path checks it
    // rather than painting over the signed-out state.
    const superseded = () => cancelled || remoteSignOutRef.current;

    async function boot() {
      let config: RuntimeConfig;
      try {
        config = await loadConfig();
      } catch (cause) {
        if (superseded()) return;
        setState({
          phase: "config-failed",
          error:
            cause instanceof RuntimeConfigError
              ? cause
              : new RuntimeConfigError(
                  "unreachable",
                  "Runtime configuration could not be loaded.",
                ),
        });
        return;
      }

      // #300: nothing is started between configuration and identity any more.
      // A branch stood here that booted an in-browser Editor Service from
      // `src/mocks` when a runtime feature flag selected it. There is one
      // runnable Editor path now — the real service — so a request that cannot
      // reach it fails visibly instead of being answered by the browser itself.

      let adapter: AuthAdapter;
      try {
        adapter = await createAuthAdapter(config);
        adapterRef.current = adapter;
        await adapter.initialize();
      } catch (cause) {
        if (superseded()) return;
        logger.error("Authentication could not be initialised", {
          errorName: cause instanceof Error ? cause.name : "unknown",
          environment: config.environment,
        });
        setState({
          phase: "auth-failed",
          error:
            cause instanceof AuthInitializationError
              ? cause
              : new AuthInitializationError(
                  "Sign-in could not be initialised.",
                ),
        });
        return;
      }

      if (cancelled) return;

      if (remoteSignOutRef.current) {
        // The remote sign-out arrived while this attempt was still loading
        // configuration or building the adapter — before there was a cache
        // to clear. There is one now: clear it and stop. Continuing would
        // restore, or automatically sign back into, the session another tab
        // just ended. Carrying the adapter gives the signed-out screen a
        // working "Sign in" instead of only a reload.
        void adapter.clearLocalSession();
        setState({ phase: "signed-out-remote", adapter });
        return;
      }

      if (!adapter.getIdentity()) {
        // A failed provider sign-out left this browser locally signed out.
        // Offer an explicit sign-in instead of auto-starting one: the Entra
        // SSO session may have survived, and an auto-started redirect would
        // silently restore the account the user just signed out.
        if (hasLocalSignOutMarker()) {
          setState({ phase: "signed-out-local", adapter });
          return;
        }
        setState({ phase: "signing-in" });
        try {
          await adapter.signIn();
        } catch (cause) {
          if (superseded()) return;
          setState({
            phase: "auth-failed",
            error: new AuthInitializationError("Sign-in could not be started.", [
              cause instanceof Error ? cause.message : "Unknown failure.",
            ]),
          });
        }
        return;
      }

      // No publishing callback is consumed here any more: the per-user GitHub
      // authorization round trip left with the delegated connection, and the
      // only redirect landing this boot resolves is MSAL's own `/auth/callback`
      // inside `adapter.initialize()` above.
      setState({ phase: "ready", config, adapter });
    }

    // Backstop. Each step above handles its own failures, but an unanticipated
    // rejection would otherwise be swallowed by `void` and strand the
    // application on the loading screen — the one outcome the fail-closed
    // design is meant to rule out.
    void boot().catch((cause: unknown) => {
      if (superseded()) return;
      logger.error("Startup failed unexpectedly", {
        errorName: cause instanceof Error ? cause.name : "unknown",
      });
      setState({
        phase: "startup-failed",
        title: "The Clinician Editor could not start",
        summary:
          "Startup stopped on an unexpected error, so no part of the editor was loaded.",
        details: [cause instanceof Error ? cause.message : "Unknown failure."],
      });
    });

    return () => {
      cancelled = true;
    };
  }, [attempt, loadConfig, createAuthAdapter]);

  const retry = () => setAttempt((value) => value + 1);

  switch (state.phase) {
    case "loading":
      return (
        <div
          role="status"
          aria-live="polite"
          className="flex min-h-screen items-center justify-center bg-background"
        >
          <p className="text-sm text-muted-foreground">
            Starting the Clinician Editor…
          </p>
        </div>
      );

    case "config-failed":
      return (
        <FailClosedScreen
          title={
            state.error.kind === "production-refused"
              ? "This environment is not supported"
              : "The editor is not configured"
          }
          summary={state.error.message}
          details={state.error.details}
          actions={
            state.error.kind === "production-refused" ? undefined : (
              <RetryButton onRetry={retry} />
            )
          }
          footer="Runtime configuration is served from /config.json by the deployment, not compiled into this build."
        />
      );

    case "auth-failed":
      return (
        <FailClosedScreen
          title="Sign-in is unavailable"
          summary={state.error.message}
          details={state.error.details}
          actions={
            <>
              <RetryButton onRetry={retry} />
              <ReloadButton />
            </>
          }
        />
      );

    case "startup-failed":
      return (
        <FailClosedScreen
          title={state.title}
          summary={state.summary}
          details={state.details}
          actions={
            <>
              <RetryButton onRetry={retry} />
              <ReloadButton />
            </>
          }
        />
      );

    case "signing-in":
      return (
        <div
          role="status"
          aria-live="polite"
          className="flex min-h-screen items-center justify-center bg-background"
        >
          <p className="text-sm text-muted-foreground">
            Redirecting you to sign in…
          </p>
        </div>
      );

    case "signed-out-local":
      return (
        <FailClosedScreen
          title="You are signed out of the editor"
          summary="Sign-out could not reach Microsoft Entra, so only this browser's session was cleared. Your account may still be signed in to Entra on this device."
          actions={
            <Button
              variant="primary"
              onClick={() => {
                clearLocalSignOutMarker();
                void state.adapter.signIn();
              }}
            >
              Sign in
            </Button>
          }
          footer="To end the Entra session as well, close every browser window or sign out of your Microsoft account."
        />
      );

    case "signed-out-remote":
      return (
        <FailClosedScreen
          title="Signed out in another tab"
          summary="This session ended because you signed out of the Clinician Editor in another tab or window."
          actions={
            state.adapter ? (
              <Button
                variant="primary"
                onClick={() => void state.adapter?.signIn()}
              >
                Sign in
              </Button>
            ) : (
              <ReloadButton />
            )
          }
        />
      );

    case "ready":
      return (
        <AppRuntime config={state.config} adapter={state.adapter} />
      );
  }
}

function AppRuntime({
  config,
  adapter,
}: {
  config: RuntimeConfig;
  adapter: AuthAdapter;
}) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const apiClient = useMemo(
    () => createApiClient(config, adapter),
    [config, adapter],
  );
  const router = useMemo(() => createBrowserRouter(routes), []);

  return (
    <RuntimeConfigProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <ApiClientProvider client={apiClient}>
            <TooltipProvider delayDuration={200}>
              <AppErrorBoundary boundary="application">
                {/* #567: in the local role-simulation lane this ensures the
                    developer's chosen profile BEFORE the session query and
                    mounts the banner; everywhere else it renders children
                    untouched (config.local is schema-refused outside local). */}
                <LocalDevProvider config={config} adapter={adapter}>
                  <SessionGate adapter={adapter}>
                    <RouterProvider router={router} />
                  </SessionGate>
                </LocalDevProvider>
              </AppErrorBoundary>
            </TooltipProvider>
        </ApiClientProvider>
      </QueryClientProvider>
    </RuntimeConfigProvider>
  );
}
